import { useEffect, useRef, useState } from 'react';

const BARKY_URL = 'https://storage.readdy-site.link/project_files/c43d99a8-1e00-428b-96a3-32143e86ff11/d6c2f9b6-4541-4f92-a27c-4e3d3602e9a5_barky_sem_fundo.png?v=6c769196e5f19b9901d880a680aabc0f';

function Stars({ count = 5, size = 16 }: { count?: number; size?: number }) {
  return (
    <div className="flex items-center" style={{ gap: '3px', marginBottom: '20px' }}>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} style={{ color: '#DC5014', fontSize: `${size}px` }}>★</span>
      ))}
    </div>
  );
}

interface BadgeProps {
  text: string;
  variant?: 'terracotta' | 'cyan' | 'teal';
}

function Badge({ text, variant = 'terracotta' }: BadgeProps) {
  const styles = {
    terracotta: {
      background: 'rgba(220,80,20,0.12)',
      border: '1px solid rgba(220,80,20,0.3)',
      color: '#DC5014',
    },
    cyan: {
      background: 'rgba(100,180,180,0.12)',
      border: '1px solid rgba(100,180,180,0.3)',
      color: '#64B4B4',
    },
    teal: {
      background: 'rgba(20,60,60,0.1)',
      border: '1px solid rgba(20,60,60,0.25)',
      color: '#143C3C',
    },
  };
  const s = styles[variant];
  return (
    <span
      style={{
        ...s,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: '10px',
        borderRadius: '20px',
        padding: '5px 14px',
        letterSpacing: '0.06em',
        textTransform: 'uppercase' as const,
        display: 'inline-block',
        whiteSpace: 'nowrap' as const,
      }}
    >
      {text}
    </span>
  );
}

export default function TestimonialsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const fadeIn = (delay = '0ms') => ({
    transition: `opacity 0.7s ease ${delay}, transform 0.7s ease ${delay}`,
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(28px)',
  });

  return (
    <section
      id="testimonials"
      ref={ref}
      className="px-5 sm:px-8 md:px-[80px]"
      style={{
        background: '#F9F9F9',
        paddingTop: '80px',
        paddingBottom: '80px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative grid — subtle on white */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(220,80,20,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(220,80,20,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10">

        {/* ── HEADER ── */}
        <div className="flex flex-col items-center text-center" style={fadeIn('0ms')}>
          <div className="flex items-center justify-center" style={{ gap: '14px', marginBottom: '28px' }}>
            <div style={{ width: '32px', height: '1px', background: '#DC5014', flexShrink: 0 }} />
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '11px',
                color: '#DC5014',
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
              }}
            >
              WHAT OUR PARTNERS SAY
            </span>
            <div style={{ width: '32px', height: '1px', background: '#DC5014', flexShrink: 0 }} />
          </div>

          <h2
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(28px, 6vw, 56px)',
              fontWeight: 700,
              color: '#0A0A0A',
              lineHeight: 1.15,
              maxWidth: '600px',
              margin: '0 auto 16px',
            }}
          >
            Real Contractors.
            <br />Real Results.
          </h2>

          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 300,
              fontSize: '16px',
              color: 'rgba(10,10,10,0.5)',
              maxWidth: '500px',
              margin: '0 auto 80px',
              lineHeight: 1.8,
            }}
          >
            These are the contractors who stopped depending on referrals and started operating like corporations.
          </p>
        </div>

        {/* ── CARD 1: GNX DEVELOPMENT — SUPREME CASE ── */}
        <div
          style={{
            ...fadeIn('100ms'),
            background: '#143C3C',
            border: '0.5px solid rgba(100,180,180,0.2)',
            marginBottom: '48px',
            position: 'relative',
            overflow: 'visible',
            cursor: 'default',
          }}
          className="px-6 py-12 md:px-[80px] md:py-[80px] group"
          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-5px)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.transform = visible ? 'translateY(0)' : 'translateY(28px)'; }}
        >
          {/* Decorative large quote */}
          <span
            className="absolute select-none pointer-events-none hidden md:block"
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: '200px',
              color: 'rgba(100,180,180,0.05)',
              top: '-20px',
              left: '32px',
              lineHeight: 1,
              zIndex: 0,
            }}
          >
            &ldquo;
          </span>

          {/* Badge top-right */}
          <div className="absolute top-4 right-4 md:top-8 md:right-8" style={{ zIndex: 3 }}>
            <Badge text="REVENUE DOUBLED: +$3M IN 6 MONTHS" variant="terracotta" />
          </div>

          <div style={{ position: 'relative', zIndex: 2 }}>
            <Stars size={18} />

            {/* Quote */}
            <p
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: 'clamp(16px, 2vw, 22px)',
                fontStyle: 'italic',
                fontWeight: 400,
                color: '#FFFFFF',
                lineHeight: 1.8,
                maxWidth: '820px',
              }}
            >
              &ldquo;We doubled our revenue in less than 6 months, surpassing{' '}
              <span style={{ color: '#DC5014', fontWeight: 700 }}>$3 million</span>{' '}
              in new contracts. The showroom and authority structure were the decisive factors in closing projects that once seemed out of reach. If you want real scale in the U.S., this is the way.&rdquo;
            </p>

            {/* Divider */}
            <div style={{ width: '48px', height: '1px', background: '#DC5014', margin: '28px 0' }} />

            {/* Footer */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                {/* Avatar */}
                <div
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#4A5D6A' }}
                >
                  <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '18px', color: '#DC5014' }}>
                    GN
                  </span>
                </div>
                <div>
                  <a
                    href="https://www.gnxconstruction.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#DC5014',
                      lineHeight: 1.2,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      display: 'block',
                    }}
                  >
                    GNX Development Team
                  </a>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', color: 'rgba(240,240,240,0.6)', marginTop: '3px' }}>
                    GNX Development · Florida &amp; Georgia
                  </p>
                </div>
              </div>

              {/* GNX right side */}
              <div className="flex flex-col items-start sm:items-end gap-4">
                <a
                  href="https://www.gnxconstruction.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  title="Visit GNX Construction"
                  style={{ opacity: 0.85, cursor: 'pointer', textDecoration: 'none' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.opacity = '1';
                    (e.currentTarget as HTMLAnchorElement).style.filter = 'drop-shadow(0 0 10px rgba(220,80,20,0.6))';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.opacity = '0.85';
                    (e.currentTarget as HTMLAnchorElement).style.filter = 'none';
                  }}
                >
                  <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '18px', color: '#DC5014', letterSpacing: '0.04em' }}>
                    GNX Construction
                  </span>
                </a>
                <a
                  href="https://www.gnxconstruction.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '12px',
                    color: '#DC5014',
                    textDecoration: 'none',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    minHeight: '44px',
                    padding: '8px 0',
                  }}
                >
                  <i className="ri-external-link-line" style={{ fontSize: '13px' }} />
                  Visit Website
                </a>
              </div>
            </div>
          </div>

          {/* Barky mascot — desktop only, positioned so it doesn't cover text */}
          <div
            className="hidden md:flex flex-col items-center"
            style={{ 
              position: 'absolute', 
              bottom: '-110px', 
              right: '40px', 
              zIndex: 10,
              pointerEvents: 'none'
            }}
          >
            <div
              style={{
                background: '#64B4B4',
                borderRadius: '12px 12px 12px 0',
                padding: '10px 16px',
                marginBottom: '6px',
                maxWidth: '180px',
                position: 'relative',
              }}
            >
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '11px', color: '#0A0A0A', lineHeight: 1.4, textAlign: 'center' }}>
                This is how we scale!<br />Are you next?
              </p>
            </div>
            <img
              src={BARKY_URL}
              alt="Barky mascot"
              style={{ width: '90px', height: 'auto', objectFit: 'contain' }}
            />
          </div>
        </div>

        {/* ── CARD 2: ES WINDOWS ── */}
        <div
          style={{
            ...fadeIn('200ms'),
            background: '#F9F9F9',
            border: '0.5px solid rgba(20,60,60,0.15)',
            marginBottom: '6px',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transform: 'scale(0.85)',
            transformOrigin: 'top center',
          }}
          className="px-6 py-10 md:px-[68px] md:py-[68px]"
          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.85) translateY(-5px)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.transform = visible ? 'scale(0.85) translateY(0)' : 'scale(0.85) translateY(28px)'; }}
        >
          <span className="absolute select-none pointer-events-none hidden md:block" style={{ fontFamily: '"Playfair Display", serif', fontSize: '200px', color: 'rgba(100,180,180,0.08)', top: '-20px', left: '32px', lineHeight: 1, zIndex: 0 }}>&ldquo;</span>

          <div className="absolute top-4 right-4 md:top-8 md:right-8" style={{ zIndex: 2 }}>
            <Badge text="INTERNATIONAL STRATEGIC PARTNER" variant="cyan" />
          </div>

          <div style={{ position: 'relative', zIndex: 2 }}>
            <Stars size={18} />

            <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(16px, 2vw, 22px)', fontStyle: 'italic', fontWeight: 400, color: 'rgba(20,60,60,0.8)', lineHeight: 1.8, maxWidth: '820px', marginBottom: '12px' }}>
              &ldquo;An international giant and strategic partner for high-volume contracts with Developers and Homebuilders.&rdquo;
            </p>

            <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '15px', color: 'rgba(20,60,60,0.6)', lineHeight: 1.8, maxWidth: '720px' }}>
              Exclusive focus on solutions for Developers and Large Construction firms across the entire American territory.
            </p>

            <div style={{ width: '48px', height: '1px', background: '#64B4B4', margin: '28px 0' }} />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <a href="https://eswindows.com/" target="_blank" rel="nofollow noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#143C3C', overflow: 'hidden' }}
                  >
                    <img src="https://storage.readdy-site.link/project_files/c43d99a8-1e00-428b-96a3-32143e86ff11/9aba818c-7d26-42a2-8eb9-1f40854de4a2_Captura-de-Tela-2026-04-03-as-14.16.02.png?v=fa3dc06ddf25b4d23726a337b9ecfb8d" alt="ES Windows logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                </a>
                <div>
                  <a href="https://eswindows.com/" target="_blank" rel="nofollow noopener noreferrer" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#143C3C', textDecoration: 'none', display: 'block' }}>
                    ES Windows
                  </a>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', color: 'rgba(20,60,60,0.55)', marginTop: '3px' }}>
                    International Partner · Colombia / USA
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-start sm:items-end gap-4">
                <a href="https://eswindows.com/" target="_blank" rel="nofollow noopener noreferrer" title="Visit ES Windows" style={{ opacity: 0.8, cursor: 'pointer', textDecoration: 'none' }}>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '18px', color: '#143C3C', letterSpacing: '0.04em' }}>ES Windows</span>
                </a>
                <a
                  href="https://eswindows.com/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '12px',
                    color: '#DC5014',
                    textDecoration: 'none',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    minHeight: '44px',
                    padding: '8px 0',
                  }}
                >
                  <i className="ri-external-link-line" style={{ fontSize: '13px' }} />
                  Visit ES Windows Official Site
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── CARD 3: CROWN STUDIO ── */}
        <div
          style={{
            ...fadeIn('300ms'),
            background: '#F9F9F9',
            border: '0.5px solid rgba(20,60,60,0.15)',
            marginBottom: '6px',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transform: 'scale(0.85)',
            transformOrigin: 'top center',
          }}
          className="px-6 py-10 md:px-[68px] md:py-[68px]"
          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.85) translateY(-5px)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.transform = visible ? 'scale(0.85) translateY(0)' : 'scale(0.85) translateY(28px)'; }}
        >
          <span className="absolute select-none pointer-events-none hidden md:block" style={{ fontFamily: '"Playfair Display", serif', fontSize: '200px', color: 'rgba(220,80,20,0.05)', top: '-20px', left: '32px', lineHeight: 1, zIndex: 0 }}>&ldquo;</span>

          <div className="absolute top-4 right-4 md:top-8 md:right-8" style={{ zIndex: 2 }}>
            <Badge text="STRATEGIC DIGITAL PARTNER" variant="terracotta" />
          </div>

          <div style={{ position: 'relative', zIndex: 2 }}>
            <Stars size={18} />

            <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(16px, 2vw, 22px)', fontStyle: 'italic', fontWeight: 400, color: 'rgba(20,60,60,0.8)', lineHeight: 1.8, maxWidth: '820px', marginBottom: '12px' }}>
              &ldquo;Before Barkfy, we were frustrated by companies that only promised results. Barkfy solved our biggest pain point: they handle the{' '}
              <span style={{ color: '#DC5014', fontWeight: 700 }}>sales support and infrastructure</span>.
              {' '}Now we focus only on growth.&rdquo;
            </p>

            <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '15px', color: 'rgba(20,60,60,0.6)', lineHeight: 1.8, maxWidth: '720px' }}>
              Crown Studio — Strategic Partner
            </p>

            <div style={{ width: '48px', height: '1px', background: '#DC5014', margin: '28px 0' }} />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <a href="https://www.crownstudio.one/" target="_blank" rel="nofollow noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ width: '52px', height: '52px', borderRadius: '50%', overflow: 'hidden', background: '#0A0A0A', border: '2px solid rgba(220,80,20,0.3)' }}
                  >
                    <img src="https://storage.readdy-site.link/project_files/c43d99a8-1e00-428b-96a3-32143e86ff11/63d22391-2a6b-4a07-8923-1208e7438374_IMG_3383.png?v=bf8389e3b78df09b25f4578b86e185bc" alt="Crown Studio logo" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
                  </div>
                </a>
                <div>
                  <a href="https://www.crownstudio.one/" target="_blank" rel="nofollow noopener noreferrer" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#143C3C', textDecoration: 'none', display: 'block' }}>
                    Crown Studio
                  </a>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', color: 'rgba(20,60,60,0.55)', marginTop: '3px' }}>
                    Design &amp; Digital Presence · Elite Partner
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-start sm:items-end gap-4">
                <a href="https://www.crownstudio.one/" target="_blank" rel="nofollow noopener noreferrer" title="Visit Crown Studio" className="flex items-center gap-2" style={{ opacity: 0.85, cursor: 'pointer', textDecoration: 'none' }}>
                  <img src="https://storage.readdy-site.link/project_files/c43d99a8-1e00-428b-96a3-32143e86ff11/63d22391-2a6b-4a07-8923-1208e7438374_IMG_3383.png?v=bf8389e3b78df09b25f4578b86e185bc" alt="Crown Studio" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                  <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '18px', color: '#143C3C', letterSpacing: '0.04em' }}>Crown Studio</span>
                </a>
                <a
                  href="https://www.crownstudio.one/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '11px',
                    color: '#FFFFFF',
                    background: '#DC5014',
                    textDecoration: 'none',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    padding: '12px 20px',
                    minHeight: '44px',
                    borderRadius: '4px',
                    transition: 'all 0.25s ease',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = '#C44410'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = '#DC5014'; }}
                >
                  <i className="ri-external-link-line" style={{ fontSize: '13px' }} />
                  VISIT WEBSITE
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── 4 SMALL CARDS — 2x2 GRID ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '6px', marginBottom: '6px' }}>

          {/* Card — Marco Rivera */}
          <div
            style={{
              ...fadeIn('300ms'),
              background: '#F9F9F9',
              border: '0.5px solid rgba(20,60,60,0.15)',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'default',
              transform: 'scale(0.85)',
              transformOrigin: 'top center',
            }}
            className="px-6 py-10 md:px-[68px] md:py-[68px]"
            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.85) translateY(-5px)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.transform = visible ? 'scale(0.85) translateY(0)' : 'scale(0.85) translateY(28px)'; }}
          >
            <span className="absolute select-none pointer-events-none" style={{ fontFamily: '"Playfair Display", serif', fontSize: '80px', color: 'rgba(220,80,20,0.06)', top: '16px', right: '20px', lineHeight: 1 }}>&ldquo;</span>
            <Stars size={14} />
            <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(15px, 1.8vw, 17px)', fontStyle: 'italic', color: 'rgba(20,60,60,0.8)', lineHeight: 1.75 }}>
              &ldquo;The showroom closed deals I couldn&apos;t have closed anywhere else. My client walked in, saw the kitchen displays, the finishes, the whole setup — and signed on the spot. No negotiation. Full price.&rdquo;
            </p>
            <div style={{ width: '32px', height: '1px', background: '#DC5014', margin: '20px 0' }} />
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#143C3C' }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#F0F0F0' }}>MR</span>
              </div>
              <div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#143C3C', lineHeight: 1.2 }}>Marco Rivera</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(20,60,60,0.5)', marginTop: '2px' }}>Orlando, FL</p>
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <Badge text="Signed on the spot — full price" variant="teal" />
            </div>
          </div>

          {/* Card — James Torres */}
          <div
            style={{
              ...fadeIn('400ms'),
              background: '#F9F9F9',
              border: '0.5px solid rgba(20,60,60,0.15)',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'default',
              transform: 'scale(0.85)',
              transformOrigin: 'top center',
            }}
            className="px-6 py-10 md:px-[68px] md:py-[68px]"
            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.85) translateY(-5px)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.transform = visible ? 'scale(0.85) translateY(0)' : 'scale(0.85) translateY(28px)'; }}
          >
            <span className="absolute select-none pointer-events-none" style={{ fontFamily: '"Playfair Display", serif', fontSize: '80px', color: 'rgba(220,80,20,0.06)', top: '16px', right: '20px', lineHeight: 1 }}>&ldquo;</span>
            <Stars size={14} />
            <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(15px, 1.8vw, 17px)', fontStyle: 'italic', color: 'rgba(20,60,60,0.8)', lineHeight: 1.75 }}>
              &ldquo;Three times last year I watched clients walk away because they couldn&apos;t pay upfront. With Barkfy&apos;s financing solution, I converted all three of those clients the following quarter. That&apos;s over $200K I would have left on the table.&rdquo;
            </p>
            <div style={{ width: '32px', height: '1px', background: '#DC5014', margin: '20px 0' }} />
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#DC5014' }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#F0F0F0' }}>JT</span>
              </div>
              <div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#143C3C', lineHeight: 1.2 }}>James Torres</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(20,60,60,0.5)', marginTop: '2px' }}>Atlanta, GA</p>
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <Badge text="$200K recovered through financing" variant="teal" />
            </div>
          </div>

          {/* Card — Carlos Mendez */}
          <div
            style={{
              ...fadeIn('500ms'),
              background: '#F9F9F9',
              border: '0.5px solid rgba(20,60,60,0.15)',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'default',
              transform: 'scale(0.85)',
              transformOrigin: 'top center',
            }}
            className="px-6 py-10 md:px-[68px] md:py-[68px]"
            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.85) translateY(-5px)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.transform = visible ? 'scale(0.85) translateY(0)' : 'scale(0.85) translateY(28px)'; }}
          >
            <span className="absolute select-none pointer-events-none" style={{ fontFamily: '"Playfair Display", serif', fontSize: '80px', color: 'rgba(220,80,20,0.06)', top: '16px', right: '20px', lineHeight: 1 }}>&ldquo;</span>
            <Stars size={14} />
            <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(15px, 1.8vw, 17px)', fontStyle: 'italic', color: 'rgba(20,60,60,0.8)', lineHeight: 1.75 }}>
              &ldquo;Before Barkfy, I was the best contractor nobody knew about. Three months after joining, I closed my first $120,000 project. The client told me: &lsquo;I chose you because you felt like a real corporation.&rsquo; That&apos;s exactly what Barkfy gave me.&rdquo;
            </p>
            <div style={{ width: '32px', height: '1px', background: '#DC5014', margin: '20px 0' }} />
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#DC5014' }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#F0F0F0' }}>CM</span>
              </div>
              <div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#143C3C', lineHeight: 1.2 }}>Carlos Mendez</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(20,60,60,0.5)', marginTop: '2px' }}>CM Premium Remodeling · Orlando, FL</p>
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <Badge text="First $120K contract in 90 days" variant="teal" />
            </div>
          </div>

          {/* Card — Ana Souza */}
          <div
            style={{
              ...fadeIn('600ms'),
              background: '#F9F9F9',
              border: '0.5px solid rgba(20,60,60,0.15)',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'default',
              transform: 'scale(0.85)',
              transformOrigin: 'top center',
            }}
            className="px-6 py-10 md:px-[68px] md:py-[68px]"
            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.85) translateY(-5px)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.transform = visible ? 'scale(0.85) translateY(0)' : 'scale(0.85) translateY(28px)'; }}
          >
            <span className="absolute select-none pointer-events-none" style={{ fontFamily: '"Playfair Display", serif', fontSize: '80px', color: 'rgba(100,180,180,0.08)', top: '16px', right: '20px', lineHeight: 1 }}>&ldquo;</span>
            <Stars size={14} />
            <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(15px, 1.8vw, 17px)', fontStyle: 'italic', color: 'rgba(20,60,60,0.8)', lineHeight: 1.75 }}>
              &ldquo;I came from Brazil with zero network in the U.S. market. Barkfy gave me a brand, a showroom and a sales team from day one. Within 4 months I had my first two contracts in Miami. I didn&apos;t build a company — I walked into one.&rdquo;
            </p>
            <div style={{ width: '32px', height: '1px', background: '#64B4B4', margin: '20px 0' }} />
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#143C3C' }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#64B4B4' }}>AS</span>
              </div>
              <div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#143C3C', lineHeight: 1.2 }}>Ana Souza</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(20,60,60,0.5)', marginTop: '2px' }}>Miami, FL · Brazilian Partner</p>
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <Badge text="2 contracts in 4 months — zero network" variant="cyan" />
            </div>
          </div>
        </div>

        {/* ── CTA BUTTON ── */}
        <div
          className="flex flex-col items-center"
          style={{
            ...fadeIn('700ms'),
            paddingTop: '56px',
          }}
        >
          <div style={{ width: '1px', height: '48px', background: '#DC5014', marginBottom: '32px' }} />
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="cursor-pointer whitespace-nowrap"
            style={{
              background: '#DC5014',
              color: '#FFFFFF',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '13px',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              padding: '0 56px',
              height: '52px',
              minHeight: '52px',
              borderRadius: '4px',
              display: 'inline-flex',
              alignItems: 'center',
              transition: 'background 0.3s ease, transform 0.3s ease',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = '#C44410';
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = '#DC5014';
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
            }}
          >
            BECOME AN ELITE PARTNER
          </a>
          <p style={{ fontFamily: 'Inter, sans-serif', fontStyle: 'italic', fontSize: '11px', color: '#64B4B4', marginTop: '14px', letterSpacing: '0.04em' }}>
            Exclusive access for Barkfy Elite Partners only.
          </p>
        </div>

      </div>
    </section>
  );
}